// @ts-check
import React, { useState } from 'react';

import Box from '@liquidweb-dev/undercurrent/Box';
import MuiCard from '@liquidweb-dev/undercurrent/Card';
import Drawer from '@liquidweb-dev/undercurrent/Drawer';
import Link from '@liquidweb-dev/undercurrent/Link';
import Skeleton from '@liquidweb-dev/undercurrent/Skeleton';
import Stack from '@liquidweb-dev/undercurrent/Stack';
import Typography from '@liquidweb-dev/undercurrent/Typography';
import ThirdpartyExclude from 'components/common/ThirdpartyExclude';
import useAccountChatPasswordGet from 'modules/queries/account/chatPassword/useGet';
import useAccountDetails from 'modules/queries/account/useDetails';
import useSupportPhoneNumbers from 'modules/queries/support/usePhoneNumbers';
import CopyButton from '@liquidweb-dev/undercurrent/CopyButton';
import DrawerContent from '@liquidweb-dev/undercurrent/DrawerContent';
import DrawerHeader from '@liquidweb-dev/undercurrent/DrawerHeader';
import EyeIcon from '@liquidweb-dev/undercurrent/icons/animated/Eye';
import PhoneIcon from '@liquidweb-dev/undercurrent/icons/lucide/Phone';

/**
 * @param {object} props
 * @param {string} [props.credentials]
 * @param {boolean} [props.isLoading]
 */
const Credentials = ({ credentials, isLoading }) => {
	const [showCredentials, setShowCredentials] = useState(false);

	return (
		<Stack
			direction="row"
			alignItems="center"
			gap={100}
			justifyContent="space-between"
		>
			{isLoading ? (
				<Skeleton variant="text" width={100} />
			) : (
				<>
					<ThirdpartyExclude>
						<Typography>
							{showCredentials ? credentials : '••••••••••••••••••••••••'}
						</Typography>
					</ThirdpartyExclude>
					<Box
						onClick={() => setShowCredentials((prev) => !prev)}
						sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
					>
						<EyeIcon
							show={showCredentials}
							sx={{
								color: ({ palette }) => palette.uc.icon.weak,
								height: ({ size }) => size.xS,
								width: ({ size }) => size.xS,
								'&:hover': {
									color: ({ palette }) => palette.uc.icon.main,
								},
							}}
						/>
					</Box>
				</>
			)}
		</Stack>
	);
};

/**
 * @param {object} props
 * @param {React.ReactNode} props.label
 * @param {React.ReactNode} props.value
 * @param {boolean} [props.isLoading]
 */
const KeyItem = ({ label, value, isLoading }) => (
	<Stack gap={50}>
		<Typography
			variant="label-xl"
			sx={{ color: ({ palette }) => palette.uc.text.weak }}
		>
			{label}
		</Typography>
		{isLoading ? <Skeleton variant="text" width={100} /> : value}
	</Stack>
);

/**
 * @param {object} props
 * @param {string} props.title
 * @param {React.ReactNode} props.children
 */
const Card = ({ title, children }) => (
	<MuiCard
		sx={{
			gap: 200,
			display: 'flex',
			flexDirection: 'column',
			padding: 200,
		}}
	>
		<Typography variant="title-xs" fontWeight={500}>
			{title}
		</Typography>
		{children}
	</MuiCard>
);

/** @param {Omit<import('@liquidweb-dev/undercurrent/Drawer').DrawerProps, 'onClose'> & { onClose: () => void }} drawerProps */
const DrawerSupportCall = ({ onClose, open, ...drawerProps }) => {
	const { data: chatPasswordData, isLoading: chatPasswordLoading } =
		useAccountChatPasswordGet({
			enabled: Boolean(open),
		});

	const { data: supportPhoneNumberData } = useSupportPhoneNumbers({
		separate_toll_free: true,
	});

	const { data: accountDetailsData, isLoading: accountDetailsLoading } =
		useAccountDetails({}, { enabled: Boolean(open) });

	/** @param {string} value */
	const handleCopy = (value) => {
		navigator.clipboard.writeText(value);
	};

	const phoneNumbers = supportPhoneNumberData?.support;

	const usPhoneNumber = Array.isArray(phoneNumbers)
		? phoneNumbers[0]
		: phoneNumbers?.toll_free?.[0] || '';

	const internationalPhoneNumber = Array.isArray(phoneNumbers)
		? phoneNumbers[1]
		: phoneNumbers?.other?.[0] || '';

	return (
		<Drawer anchor="right" onClose={onClose} open={open} {...drawerProps}>
			<DrawerHeader
				onClose={onClose}
				description="Everything you need to get the conversation started, all in one place"
			>
				Give us a call
			</DrawerHeader>

			<DrawerContent>
				{(usPhoneNumber || internationalPhoneNumber) && (
					<Card title="Contact info">
						{usPhoneNumber && (
							<Stack gap={50}>
								<KeyItem
									label={
										<Stack direction="row" gap={50} alignItems="center">
											<PhoneIcon />
											US Phone
										</Stack>
									}
									value={
										<Stack
											direction="row"
											gap={100}
											alignItems="center"
											justifyContent="space-between"
										>
											<Link
												href={`tel:${usPhoneNumber}`}
												variant="body-md"
												fontWeight={500}
											>
												{usPhoneNumber}
											</Link>
											<CopyButton
												onClick={() => handleCopy(usPhoneNumber)}
												iconButtonProps={{ variant: 'barebones' }}
												tooltipProps={{ placement: 'right' }}
											/>
										</Stack>
									}
								/>
							</Stack>
						)}

						{internationalPhoneNumber && (
							<Stack gap={50}>
								<KeyItem
									label={
										<Stack direction="row" gap={50} alignItems="center">
											<PhoneIcon />
											International Phone
										</Stack>
									}
									value={
										<Stack
											direction="row"
											gap={100}
											alignItems="center"
											justifyContent="space-between"
										>
											<Link
												href={`tel:${internationalPhoneNumber}`}
												variant="body-md"
												fontWeight={500}
											>
												{internationalPhoneNumber}
											</Link>
											<CopyButton
												onClick={() => handleCopy(internationalPhoneNumber)}
												iconButtonProps={{ variant: 'barebones' }}
												tooltipProps={{ placement: 'right' }}
											/>
										</Stack>
									}
								/>
							</Stack>
						)}
					</Card>
				)}

				<Card title="Account info">
					<KeyItem
						label="Acct. ID"
						value={accountDetailsData?.accnt}
						isLoading={accountDetailsLoading}
					/>
					<KeyItem
						label="Support passphrase"
						value={
							<Credentials
								credentials={chatPasswordData?.password}
								isLoading={chatPasswordLoading}
							/>
						}
					/>
				</Card>
			</DrawerContent>
		</Drawer>
	);
};

export default DrawerSupportCall;
